import { ReactNode, useCallback, useContext, useMemo } from "react";
import { ActivityLoggerProvider } from "activity-logger/react";
import { ErrorBoundary } from "react-error-boundary";
import { ApiService } from "../../services/api/service";
import { ApiContext } from "../../contexts/app/context";
import { isActivityLoggingEnabled } from "./config";
import { createActivitySender } from "./sender";
import { PrivacySafeRouteTracker } from "./route_tracker";
import { effectiveActivityCustomerId } from "./tenant";

const ActivityLoggingGeneration = ({
  apiService,
  customerId,
}: {
  apiService: ApiService;
  customerId: string;
}) => {
  const sender = useMemo(
    () => createActivitySender(apiService, customerId),
    [apiService, customerId],
  );
  const getContext = useCallback(() => ({ client: customerId }), [customerId]);
  const onError = useCallback(() => {
    // Product telemetry is deliberately best-effort. Do not log raw events,
    // URLs, parameters, email, or the transport error response here.
    console.info("Activity logging failed");
  }, []);

  return (
    <ErrorBoundary
      fallback={null}
      onError={() => console.info("Activity logging disabled")}
    >
      <ActivityLoggerProvider
        app="common"
        send={sender}
        getContext={getContext}
        onError={onError}
      >
        <PrivacySafeRouteTracker />
      </ActivityLoggerProvider>
    </ErrorBoundary>
  );
};

export const ActivityLogging = ({
  children,
  apiService: apiServiceOverride,
  customerId: customerIdOverride,
  enabled = isActivityLoggingEnabled(),
}: {
  children: ReactNode;
  apiService?: ApiService;
  customerId?: string | null;
  enabled?: boolean;
}) => {
  const { apiService, actualRole, selectedCustomerId, whoIAm } =
    useContext(ApiContext);
  const contextCustomerId = effectiveActivityCustomerId({
    actualRole,
    selectedCustomerId,
    userCustomerId: whoIAm?.customerId,
  });
  const customerId =
    customerIdOverride === undefined ? contextCustomerId : customerIdOverride;
  const senderApiService = apiServiceOverride ?? apiService;

  return (
    <>
      {enabled && customerId ? (
        <ActivityLoggingGeneration
          key={customerId}
          apiService={senderApiService}
          customerId={customerId}
        />
      ) : null}
      {children}
    </>
  );
};

export { createActivitySender } from "./sender";
export { effectiveActivityCustomerId } from "./tenant";
